import { useState } from 'react';
import { Calculator, Zap, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Investor Cashback Tiers (Based on Investment Amount)
// Base: ₹0, Silver: ₹10L, Gold: ₹30L, Platinum: ₹90L, Diamond: ₹2.7Cr
const investorTiers = [
  { name: 'Base', threshold: 0, cashback: 0.5, icon: '🌱', color: 'text-gray-400', bgColor: 'bg-gray-400/10' },
  { name: 'Silver', threshold: 1000000, cashback: 1.0, icon: '🥈', color: 'text-gray-300', bgColor: 'bg-gray-300/10' },      // ₹10 Lakh
  { name: 'Gold', threshold: 3000000, cashback: 1.5, icon: '🥇', color: 'text-yellow-400', bgColor: 'bg-yellow-400/10' },    // ₹30 Lakh
  { name: 'Platinum', threshold: 9000000, cashback: 2.0, icon: '💎', color: 'text-cyan-400', bgColor: 'bg-cyan-400/10' },    // ₹90 Lakh
  { name: 'Diamond', threshold: 27000000, cashback: 3.0, icon: '👑', color: 'text-purple-400', bgColor: 'bg-purple-400/10' }, // ₹2.7 Crore
];

export default function HeroCalculator() {
  const [investmentAmount, setInvestmentAmount] = useState(1000000);
  const [showTierBenefits, setShowTierBenefits] = useState(false);

  const property = {
    name: 'Premium Skyline Tower',
    assetValue: 48000000,
    capitalTarget: 12000000,
    capitalRaised: 3600000,
  };

  const balanceToFund = property.capitalTarget - property.capitalRaised;
  const fundingPercentage = (property.capitalRaised / property.capitalTarget) * 100;
  const ownershipPercentage = (investmentAmount / property.assetValue) * 100;

  const getCurrentTier = (amount) => {
    for (let i = investorTiers.length - 1; i >= 0; i--) {
      if (amount >= investorTiers[i].threshold) {
        return { tier: investorTiers[i], index: i };
      }
    }
    return { tier: investorTiers[0], index: 0 };
  };

  const { tier: currentTier, index: currentTierIndex } = getCurrentTier(investmentAmount);
  const nextTier = currentTierIndex < investorTiers.length - 1 ? investorTiers[currentTierIndex + 1] : null;
  const cashbackAmount = (investmentAmount * currentTier.cashback) / 100;
  const progressToNextTier = nextTier ? ((investmentAmount - currentTier.threshold) / (nextTier.threshold - currentTier.threshold)) * 100 : 100;
  const amountToNextTier = nextTier ? nextTier.threshold - investmentAmount : 0;

  const minInvestment = 500000;
  const maxInvestment = balanceToFund;

  const formatINR = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  return (
    <div className="w-full max-w-sm bg-white/5 backdrop-blur-md border border-white/10">
      <div className="bg-white/5 border-b border-white/10 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#4ADE80]/20 flex items-center justify-center">
              <Calculator className="w-3.5 h-3.5 text-[#4ADE80]" />
            </div>
            <h3 className="text-sm font-medium text-white">Calculator</h3>
          </div>
          <button onClick={() => setShowTierBenefits(!showTierBenefits)} className="text-xs text-[#4ADE80]">
            {showTierBenefits ? 'Hide' : 'Tiers'}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showTierBenefits && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="bg-black border-b border-white/10 overflow-hidden">
            <div className="px-5 py-4">
              <p className="text-xs text-gray-500 uppercase mb-4">Rewards</p>
              {investorTiers.map((tier) => (
                <div key={tier.name} className="flex justify-between py-2">
                  <div className="flex items-center gap-2">
                    <span>{tier.icon}</span>
                    <p className="text-sm text-gray-400">{tier.name}</p>
                  </div>
                  <p className="text-sm text-gray-400">{tier.cashback}%</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white/5 px-5 py-4 border-b border-white/10">
        <div className="flex justify-between mb-4">
          <p className="text-sm text-[#4ADE80]">{property.name}</p>
          <div className="flex items-center gap-1 px-2 py-1 bg-white/5">
            <span>{currentTier.icon}</span>
            <span className="text-xs text-white">{currentTier.name}</span>
          </div>
        </div>

        {nextTier && (
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-2">To {nextTier.name}: {formatINR(amountToNextTier)}</p>
            <div className="h-1 bg-white/10">
              <div className="h-full bg-[#4ADE80]" style={{ width: `${progressToNextTier}%` }} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Unit Value</p>
            <p className="font-mono text-white">{formatINR(property.assetValue)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Target</p>
            <p className="font-mono text-white">{formatINR(property.capitalTarget)}</p>
          </div>
        </div>

        <div>
          <div className="flex justify-between text-xs mb-2">
            <span className="text-gray-500">Funding</span>
            <span className="text-[#4ADE80]">{fundingPercentage.toFixed(0)}%</span>
          </div>
          <div className="h-px bg-white/10">
            <div className="h-full bg-[#4ADE80]" style={{ width: `${fundingPercentage}%` }} />
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="mb-5">
          <div className="flex justify-between mb-3">
            <span className="text-sm text-gray-400">Investment</span>
            <span className="text-xl font-mono text-white">{formatINR(investmentAmount)}</span>
          </div>
          <input type="range" min={minInvestment} max={maxInvestment} step={100000} value={investmentAmount} onChange={(e) => setInvestmentAmount(parseInt(e.target.value))} className="w-full h-px bg-white/10 slider-thumb" />
          <div className="flex justify-between text-xs text-gray-600 mt-2">
            <span>{formatINR(minInvestment)}</span>
            <span>{formatINR(maxInvestment)}</span>
          </div>
        </div>

        <div className="bg-[#4ADE80]/10 border border-[#4ADE80]/30 p-4 mb-4">
          <p className="text-xs text-gray-400 mb-1">Your Share</p>
          <p className="text-4xl font-mono text-[#4ADE80]">{ownershipPercentage.toFixed(1)}%</p>
        </div>

        <div className="bg-purple-500/10 border border-purple-500/30 p-4">
          <div className="flex justify-between mb-2">
            <p className="text-xs text-gray-400">Cashback</p>
            <span className="text-xs text-gray-500">{currentTier.cashback}%</span>
          </div>
          <p className="text-2xl font-mono text-[#4ADE80]">{formatINR(cashbackAmount)}</p>
        </div>
      </div>
    </div>
  );
}
